// react                                                                                                                                                                                                            │ │
import React, { useEffect, useState } from 'react'

// i18n
import 'src/locales/i18n'

// scrollbar
import 'simplebar-react/dist/simplebar.min.css'

// lightbox
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

// editor
import 'react-quill-new/dist/quill.snow.css'

// carousel
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// image
import 'react-lazy-load-image-component/src/effects/blur.css'

// config - AJOUTEZ CETTE LIGNE
import { NETSUITE_API, POSTHOG_API, SENTRY_API, ENV } from 'src/config-global'

// Sentry
import * as Sentry from '@sentry/react';

// posthog
import posthog from 'posthog-js'
import { PostHogProvider } from '@posthog/react'

// ----------------------------------------------------------------------

// mui
import Button from '@mui/material/Button';
// routes
import Router from 'src/routes/sections'
// theme
import ThemeProvider from 'src/theme'
// locales
import { LocalizationProvider, useLocales } from 'src/locales'
// hooks
import { useScrollToTop } from 'src/hooks/use-scroll-to-top'
import { useBoolean } from 'src/hooks';
// components
import ProgressBar from 'src/components/progress-bar';
import { MotionLazy } from 'src/components/animate/motion-lazy';
import SnackbarProvider from 'src/components/snackbar/snackbar-provider';
import { SettingsProvider, SettingsDrawer } from 'src/components/settings';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { AppUpdateManager } from 'src/components/app-update-manager';
// auth
// import { AuthProvider, AuthConsumer } from 'src/auth/context/jwt'
// import { AuthProvider, AuthConsumer } from 'src/auth/context/auth0'
import { AuthProvider, AuthConsumer } from 'src/auth/context/amplify'
// import { AuthProvider, AuthConsumer } from 'src/auth/context/firebase'
// Layout
import StartLayout from 'src/layouts/start'
// redux
import ReduxProvider from './redux/redux-provider';
import WsProvider from './websocket/ws-provider';
import { OfflineProvider } from './offline';
// stripe
import { StripeProvider } from './stripe'
import { firstLetterToUpperCase } from './utils/change-case';

// ----------------------------------------------------------------------

if (typeof window !== 'undefined' && POSTHOG_API.key && POSTHOG_API.host) {
  const isLocal =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (!isLocal) {
    posthog.init(POSTHOG_API.key, {
      api_host: POSTHOG_API.host,
      defaults: '2026-01-30',
      person_profiles: 'identified_only',
      capture_pageview: false, // On laisse le PostHogProvider gérer ça s'il est configuré pour, ou on le fait manuellement
    })
  }
}

if (SENTRY_API.dsn) {
  // Ne pas initialiser Sentry en local dev pour de meilleures performances
  const isLocal =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  if (!isLocal) {
    Sentry.init({
      dsn: SENTRY_API.dsn,
      environment: ENV,
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      // Performance Monitoring - 10% seulement pour ne pas ralentir l'app
      tracesSampleRate: 0.1,
      // Session Replay
      replaysSessionSampleRate: 0.05, // 5% seulement
      replaysOnErrorSampleRate: 1.0, // 100% on error
    });
  }
}

export default function App() {
  /*
  const charAt = `

  ░░░    ░░░
  ▒▒▒▒  ▒▒▒▒
  ▒▒ ▒▒▒▒ ▒▒
  ▓▓  ▓▓  ▓▓
  ██      ██

  `

  console.info(`%c${charAt}`, 'color: #5BE49B')
  */

  const { t } = useLocales();
  const confirm = useBoolean();

  // État pour suivre si l'utilisateur a des changements non sauvegardés
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const refreshDialog = (
    <ConfirmDialog
      open={confirm.value}
      onClose={confirm.onFalse}
      title={firstLetterToUpperCase(t('refresh_app'))}
      content={firstLetterToUpperCase(t('an_update_is_available_please_refresh'))}
      action={
        <Button variant="contained" color="success" onClick={() => window.location.reload()}>
          {firstLetterToUpperCase(t('ok'))}
        </Button>
      }
      secondaryAction={
        <Button variant="contained" color="error" onClick={() => {}}>
          {firstLetterToUpperCase(t('cancel'))}
        </Button>
      }
    />
  );

  window.addEventListener('error', (event) => {
    if (event.message?.includes('Failed to fetch dynamically imported module')) {
      confirm.onTrue();
    }
  });

  useScrollToTop();

    useEffect(() => {
      // Keep-alive uniquement en production, pas en local dev
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isLocal) return;

      const keepAlive = setInterval(
        () => {
          fetch(`${NETSUITE_API.host}/health`).catch((err) =>
            console.log('Keep-alive ping failed:', err)
          );
        },
        10 * 60 * 1000 // 10 minutes au lieu de 5
      );

      return () => clearInterval(keepAlive);
    }, []);

    return (
      <PostHogProvider client={posthog}>
        <ReduxProvider>
          <AuthProvider>
            <LocalizationProvider>
              <SettingsProvider
                defaultSettings={{
                  themeMode: 'light', // 'light' | 'dark'
                  themeDirection: 'ltr', //  'rtl' | 'ltr'
                  themeContrast: 'default', // 'default' | 'bold'
                  themeLayout: 'vertical', // 'vertical' | 'horizontal' | 'mini'
                  themeColorPresets: 'default', // 'default' | 'cyan' | 'purple' | 'blue' | 'orange' | 'red'
                  themeStretch: false,
                }}
              >
                <ThemeProvider>
                  <MotionLazy>
                    <SnackbarProvider>
                      <SettingsDrawer />
                      <ProgressBar />
                      <AuthConsumer>
                        <StripeProvider>
                          <WsProvider>
                            <OfflineProvider>
                              <StartLayout>
                                {refreshDialog}
                                <AppUpdateManager hasUnsavedChanges={hasUnsavedChanges} />
                                <Router />
                              </StartLayout>
                            </OfflineProvider>
                          </WsProvider>
                        </StripeProvider>
                      </AuthConsumer>
                    </SnackbarProvider>
                  </MotionLazy>
                </ThemeProvider>
              </SettingsProvider>
            </LocalizationProvider>
          </AuthProvider>
        </ReduxProvider>
      </PostHogProvider>
    );
}
